// Single source of truth for feedback attachments (K-93): which files are allowed, how big they can
// be, and how each type is verified and previewed. The zod schema, the uploader and the preview all
// derive from this module, so adding a format or changing a limit is a one-place edit.

// A signature is a list of [offset, bytes] pairs that must all match; a type accepts a file when
// any one of its signatures matches.
type Signature = Array<[number, number[]]>

// What a browser can render natively. HEIC/HEIF and Office documents have no in-browser renderer.
export type FeedbackPreviewKind = 'image' | 'video' | 'pdf' | 'none'

interface FeedbackAttachmentType {
  mimeTypes: string[]
  signatures: Signature[]
  preview: FeedbackPreviewKind
}

const ascii = (value: string) => Array.from(value, (char) => char.charCodeAt(0))

const JPEG: Signature = [[0, [0xff, 0xd8, 0xff]]]
// ISO base media (MP4, MOV, HEIC/HEIF) carries its brand box at offset 4
const FTYP: Signature = [[4, ascii('ftyp')]]
// Pre-ftyp QuickTime files open straight on a movie, data or padding atom
const QUICKTIME_ATOMS = ['moov', 'mdat', 'wide', 'free', 'skip', 'pnot'].map<Signature>((atom) => [[4, ascii(atom)]])
// .docx/.xlsx/.pptx are ZIP containers
const ZIP: Signature = [[0, [0x50, 0x4b, 0x03, 0x04]]]

// Allowlist per K-93: images (incl. iOS HEIC/HEIF), screen-recording video, and the document types
// already accepted by file training. Anything not listed is rejected.
const FEEDBACK_ATTACHMENT_TYPES: Record<string, FeedbackAttachmentType> = {
  '.jpg': { mimeTypes: ['image/jpeg'], signatures: [JPEG], preview: 'image' },
  '.jpeg': { mimeTypes: ['image/jpeg'], signatures: [JPEG], preview: 'image' },
  '.png': {
    mimeTypes: ['image/png'],
    signatures: [[[0, [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a]]]],
    preview: 'image',
  },
  '.webp': {
    mimeTypes: ['image/webp'],
    signatures: [
      [
        [0, ascii('RIFF')],
        [8, ascii('WEBP')],
      ],
    ],
    preview: 'image',
  },
  '.heic': { mimeTypes: ['image/heic'], signatures: [FTYP], preview: 'none' },
  '.heif': { mimeTypes: ['image/heif'], signatures: [FTYP], preview: 'none' },
  '.gif': { mimeTypes: ['image/gif'], signatures: [[[0, ascii('GIF8')]]], preview: 'image' },
  '.mp4': { mimeTypes: ['video/mp4'], signatures: [FTYP], preview: 'video' },
  '.mov': { mimeTypes: ['video/quicktime'], signatures: [FTYP, ...QUICKTIME_ATOMS], preview: 'video' },
  '.webm': { mimeTypes: ['video/webm'], signatures: [[[0, [0x1a, 0x45, 0xdf, 0xa3]]]], preview: 'video' },
  '.pdf': { mimeTypes: ['application/pdf'], signatures: [[[0, ascii('%PDF-')]]], preview: 'pdf' },
  '.docx': {
    mimeTypes: ['application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
    signatures: [ZIP],
    preview: 'none',
  },
  '.xlsx': {
    mimeTypes: ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'],
    signatures: [ZIP],
    preview: 'none',
  },
  '.pptx': {
    mimeTypes: ['application/vnd.openxmlformats-officedocument.presentationml.presentation'],
    signatures: [ZIP],
    preview: 'none',
  },
}

export const FEEDBACK_ATTACHMENT_ALLOWED_FORMATS = Object.keys(FEEDBACK_ATTACHMENT_TYPES)
export const FEEDBACK_ATTACHMENT_ALLOWED_MIME_TYPES = Array.from(
  new Set(Object.values(FEEDBACK_ATTACHMENT_TYPES).flatMap((type) => type.mimeTypes)),
)

export const FEEDBACK_ATTACHMENT_MAX_FILES = 5
export const FEEDBACK_ATTACHMENT_MAX_FILE_SIZE_MB = 10
// Capped by email deliverability, not storage: base64 encoding inflates attachments by ~33%,
// so 15MB of files lands at ~20MB on the wire, under Gmail's 25MB limit. Confirmed by the PM
// against the actual notification recipient list (see K-93).
export const FEEDBACK_ATTACHMENT_MAX_TOTAL_SIZE_MB = 15

export const BYTES_PER_MB = 1024 * 1024

export const getFeedbackFileExtension = (file: File) => `.${file.name.split('.').pop()}`.toLowerCase()

const attachmentTypeOf = (file: File) => FEEDBACK_ATTACHMENT_TYPES[getFeedbackFileExtension(file)]

export const feedbackPreviewKind = (file: File): FeedbackPreviewKind => attachmentTypeOf(file)?.preview ?? 'none'

export const feedbackFileKey = (file: File) => `${file.name}-${file.size}`

export const totalFeedbackAttachmentBytes = (files: File[]) => files.reduce((total, file) => total + file.size, 0)

export const isWithinFeedbackFileSizeLimit = (file: File) =>
  file.size <= FEEDBACK_ATTACHMENT_MAX_FILE_SIZE_MB * BYTES_PER_MB

export const isWithinFeedbackTotalSizeLimit = (totalBytes: number) =>
  totalBytes <= FEEDBACK_ATTACHMENT_MAX_TOTAL_SIZE_MB * BYTES_PER_MB

// The extension must be on the allowlist and the MIME type the browser reports must agree with it.
// This only catches an OS/extension mismatch: browsers derive `type` from the extension, so a
// renamed file passes here and is caught by the byte signature below. An empty or generic type means
// the browser could not classify the file (common for HEIC/HEIF and .mov), so the extension decides
// and a legitimate iPhone upload is not rejected.
export const hasAllowedFeedbackFileType = (file: File) => {
  const type = attachmentTypeOf(file)
  if (!type) return false
  if (!file.type || file.type === 'application/octet-stream') return true

  return type.mimeTypes.includes(file.type)
}

// Enough leading bytes to evaluate every signature in one read
const HEADER_BYTES = Math.max(
  ...Object.values(FEEDBACK_ATTACHMENT_TYPES).flatMap((type) =>
    type.signatures.flatMap((signature) => signature.map(([offset, bytes]) => offset + bytes.length)),
  ),
)

// FileReader rather than Blob.arrayBuffer(): identical in browsers, and also available in jsdom
const readHeader = (file: File): Promise<Uint8Array> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(new Uint8Array(reader.result as ArrayBuffer))
    reader.onerror = () => reject(reader.error)
    reader.readAsArrayBuffer(file.slice(0, HEADER_BYTES))
  })

const matchesSignature = (header: Uint8Array, signature: Signature) =>
  signature.every(([offset, bytes]) => bytes.every((byte, index) => header[offset + index] === byte))

// Catches a renamed file (e.g. virus.exe -> photo.jpg) on the client; the server validates independently
const hasMatchingSignature = async (file: File) => {
  const signatures = attachmentTypeOf(file)?.signatures
  if (!signatures) return false

  try {
    const header = await readHeader(file)
    return signatures.some((signature) => matchesSignature(header, signature))
  } catch {
    // an unreadable file cannot be verified, so it is not accepted
    return false
  }
}

export interface FeedbackAttachmentRejection {
  file: File
  reason: 'type' | 'size' | 'count' | 'total'
}

/**
 * Decides, at the moment files are picked or dropped, which of them join the current selection.
 * Files are checked in order against type, per-file size, count and running total, so the first
 * files that fit are kept and only the overflow is rejected. Duplicates (same name and size as a
 * file already selected) are skipped silently.
 */
export const screenFeedbackAttachments = async (existing: File[], incoming: File[]) => {
  const seen = new Set(existing.map(feedbackFileKey))
  const unique = incoming.filter((file) => {
    const key = feedbackFileKey(file)
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })

  // Signature reads are the only async step; run them together, then decide sequentially
  const typeChecks = await Promise.all(
    unique.map(async (file) => hasAllowedFeedbackFileType(file) && (await hasMatchingSignature(file))),
  )

  const accepted: File[] = []
  const rejected: FeedbackAttachmentRejection[] = []
  let runningCount = existing.length
  let runningBytes = totalFeedbackAttachmentBytes(existing)

  unique.forEach((file, index) => {
    let reason: FeedbackAttachmentRejection['reason'] | null = null
    if (!typeChecks[index]) reason = 'type'
    else if (!isWithinFeedbackFileSizeLimit(file)) reason = 'size'
    else if (runningCount >= FEEDBACK_ATTACHMENT_MAX_FILES) reason = 'count'
    else if (!isWithinFeedbackTotalSizeLimit(runningBytes + file.size)) reason = 'total'

    if (reason) {
      rejected.push({ file, reason })
      return
    }

    accepted.push(file)
    runningCount += 1
    runningBytes += file.size
  })

  return { accepted, rejected }
}
